import {
  fetchListingsResult,
  deriveFeedState,
  matchesCity,
  matchesMinBudget,
  MAX_PAGE_SIZE,
  BrowseStatus,
} from '@/lib/marketplace';
import { locationLabel } from '@/lib/listingSettings';

import { Listing } from '@/components/JobCard';


/**
 * PERMANENT MOBILE BROWSE REGRESSION SUITE
 * =========================================
 * Guards the launch blocker: "51 listings in the database, Zero listings on
 * mobile". Run it from Admin -> Security after any change to the browse path,
 * the rate limiter, the masked view or the marketplace page.
 *
 * It deliberately does NOT assert a fixed listing count (the catalog changes) —
 * it compares what the UI would render against what the API actually returned.
 */

export interface RegressionCase {
  name: string;
  passed: boolean;
  detail: string;
}

/** Fields that must NEVER appear with a value in a public browse payload. */
const FORBIDDEN_FIELDS = [
  'owner_id', 'buyer_id', 'user_id', 'contact_name', 'contact_email', 'contact_phone',
  'phone', 'email', 'address', 'notes', 'internal_notes', 'documents', 'document_path',
  'signed_url', 'stripe_customer_id', 'payment_intent_id',
];

const leaks = (rows: Listing[]): string[] => {
  const found = new Set<string>();
  rows.forEach((r) => {
    const obj = r as unknown as Record<string, unknown>;
    FORBIDDEN_FIELDS.forEach((f) => {
      if (obj[f] !== undefined && obj[f] !== null && obj[f] !== '') found.add(f);
    });
    // CITY IS PUBLIC (cards show "Fontana, CA"). ZIP is not: it is the field
    // that narrows a project to a block. After the audit the public payload has
    // no `zip` KEY at all (dropped from the edge-function allow-list AND hard
    // nulled in mkt_listings_public), so ANY value here is a regression.
    if (obj.zip !== undefined && obj.zip !== null && obj.zip !== '') found.add('zip');

  });
  return [...found];
};


const pass = (name: string, detail: string): RegressionCase => ({ name, passed: true, detail });
const fail = (name: string, detail: string): RegressionCase => ({ name, passed: false, detail });

/**
 * Pure state-machine checks. These run with no network and catch the exact class
 * of bug that caused the outage: a failure being rendered as an empty catalog,
 * and the follow-up outage: a failure being rendered as an endless spinner.
 */
export const runStateMachineCases = (): RegressionCase[] => {
  const cases: RegressionCase[] = [];

  // 1. Successful request with rows -> cards render (loading is left behind).
  cases.push(
    deriveFeedState('ok', 51) === 'ok'
      ? pass('Success -> loading off, cards render', '51 rows -> "ok"')
      : fail('Success -> loading off, cards render', 'listings present but UI would not render them'),
  );

  // 2. Successful request with zero rows -> empty state (loading is left behind).
  cases.push(
    deriveFeedState('ok', 0) === 'empty'
      ? pass('Empty result -> loading off, empty state', 'only a successful 0-row response shows "empty"')
      : fail('Empty result -> loading off, empty state', 'unexpected state'),
  );

  // 3. Every failure verdict -> its own message + Retry, never "empty",
  //    and critically never "loading".
  const failureModes: BrowseStatus[] = ['rate_limited', 'network_error', 'server_error'];
  const wrong = failureModes.filter((s) => deriveFeedState(s, 0) === 'empty');
  const stuck = failureModes.filter((s) => {
    const next = deriveFeedState(s, 0);
    return next === 'loading' || next === 'ok';
  });
  cases.push(
    wrong.length === 0
      ? pass('Failures never render "Zero listings"', 'rate_limited / network_error / server_error keep their own message + Retry')
      : fail('Failures never render "Zero listings"', `these were shown as empty: ${wrong.join(', ')}`),
  );
  cases.push(
    stuck.length === 0
      ? pass('Failed request -> loading off, retry UI', 'every failure verdict resolves to an error card with a Retry button')
      : fail('Failed request -> loading off, retry UI', `these would strand the spinner: ${stuck.join(', ')}`),
  );

  // 4. An EMPTY City box must not filter anything out. This is the guard for
  //    "/jobs stuck on Loading projects…": the city control must never be able
  //    to touch, block or empty the initial feed.
  const sample: Array<Partial<Listing>> = [
    { id: '1', city: 'Fontana', state: 'CA' },
    { id: '2', city: null, state: 'PA' },
    { id: '3', city: ' Perry Hall', state: 'MD' },
  ];
  const noFilter = sample.filter((l) => matchesCity(l.city, ''));
  const blankSpaces = sample.filter((l) => matchesCity(l.city, '   '));
  cases.push(
    noFilter.length === sample.length && blankSpaces.length === sample.length
      ? pass('Empty City input adds no filter', `blank and whitespace-only input keep all ${sample.length} rows, including the null-city row`)
      : fail('Empty City input adds no filter', `blank input dropped rows (${noFilter.length}/${sample.length})`),
  );

  // 5. City filtering itself works — case, padding and partials all match, and
  //    a null city simply does not match rather than throwing.
  const fontana = sample.filter((l) => matchesCity(l.city, 'fontana'));
  const perry = sample.filter((l) => matchesCity(l.city, 'perry hall'));
  const nomatch = sample.filter((l) => matchesCity(l.city, 'zzz'));
  cases.push(
    fontana.length === 1 && perry.length === 1 && nomatch.length === 0
      ? pass('City filter works without blocking load', 'case-insensitive, whitespace-tolerant, null-city safe; applied in memory after the fetch')
      : fail('City filter works without blocking load', `fontana=${fontana.length} perry=${perry.length} nomatch=${nomatch.length}`),
  );

  // 6. LOCATION LABEL — "City, ST" everywhere, full state name as the fallback,
  //    and never "null, CA" / "undefined, CA" / ", CA".
  const labelCases: Array<[{ city?: string | null; state?: string | null }, string]> = [
    [{ city: 'Fontana', state: 'CA' }, 'Fontana, CA'],
    [{ city: 'Los Angeles', state: 'ca' }, 'Los Angeles, CA'],
    [{ city: 'Allentown', state: 'Pennsylvania' }, 'Allentown, PA'],
    [{ city: ' Perry Hall ', state: 'md' }, 'Perry Hall, MD'],
    [{ city: null, state: 'CA' }, 'California'],
    [{ city: '', state: 'PA' }, 'Pennsylvania'],
    [{ city: 'null', state: 'CA' }, 'California'],
    [{ city: 'undefined', state: 'CA' }, 'California'],
  ];
  const badLabels = labelCases.filter(([input, expected]) => locationLabel(input) !== expected);
  cases.push(
    badLabels.length === 0
      ? pass('City + State label', '"City, ST", full-state fallback, dirty/null/undefined city all handled')
      : fail('City + State label', badLabels.map(([i, e]) => `${JSON.stringify(i)} -> "${locationLabel(i)}" (expected "${e}")`).join('; ')),
  );

  // 7. MINIMUM BUDGET filter. The old implementation parsed the first 6 digits
  //    of the range string, so "$5,000 - $15,000" became 500001 and every
  //    listing passed every threshold — the control did nothing at all.
  const budgetRows = [
    { budget_range: 'Under $5,000' },      // ceiling 5000
    { budget_range: '$5,000 - $15,000' },  // ceiling 15000
    { budget_range: '$30,000 - $60,000' }, // ceiling 60000
    { budget_range: null },                // unknown -> always kept
  ];
  const over30k = budgetRows.filter((r) => matchesMinBudget(r, 30000));
  const noFloor = budgetRows.filter((r) => matchesMinBudget(r, ''));
  cases.push(
    over30k.length === 2 && noFloor.length === budgetRows.length
      ? pass('Minimum budget filter works', '$30k+ keeps the $60k row and the unknown-budget row; "All" keeps everything')
      : fail('Minimum budget filter works', `30k kept ${over30k.length}/4 (expected 2), "All" kept ${noFloor.length}/4`),
  );

  // 8. Filters COMBINE correctly (city + budget together).
  const combo = [
    { city: 'Miami', budget_range: '$30,000 - $60,000' },
    { city: 'Miami', budget_range: 'Under $5,000' },
    { city: 'Tampa', budget_range: '$30,000 - $60,000' },
  ].filter((r) => matchesCity(r.city, 'miami') && matchesMinBudget(r, 30000));
  cases.push(
    combo.length === 1
      ? pass('Combined filters', 'city + minimum budget intersect instead of overriding each other')
      : fail('Combined filters', `expected 1 row, got ${combo.length}`),
  );

  return cases;

};


/**
 * Live checks against the production browse path, using the SAME payload the
 * mobile client sends.
 */
export const runLiveCases = async (): Promise<RegressionCase[]> => {
  const cases: RegressionCase[] = [];

  // 1. Default mobile request — no filters, page 0. This is the exact call the
  //    marketplace makes on first paint.
  const first = await fetchListingsResult({ limit: MAX_PAGE_SIZE, offset: 0 });
  if (first.status !== 'ok') {
    cases.push(fail('Default feed loads', `status="${first.status}" (retryAfter ${first.retryAfter}s)`));
    return cases;
  }
  cases.push(pass('Default feed loads', `${first.listings.length} listing(s) returned with no filters applied`));

  // 2. UI must agree with the API.
  const state = deriveFeedState(first.status, first.listings.length);
  cases.push(
    first.listings.length > 0 && state === 'ok'
      ? pass('UI renders what the API returned', `${first.listings.length} card(s) would render`)
      : first.listings.length === 0
        ? pass('UI renders what the API returned', 'API genuinely returned 0 rows')
        : fail('UI renders what the API returned', 'API returned rows but the UI state was not "ok"'),
  );

  // 3. Server-side row cap still enforced.
  const over = await fetchListingsResult({ limit: 500, offset: 0 });
  cases.push(
    over.status === 'ok' && over.listings.length <= MAX_PAGE_SIZE
      ? pass('60-row server cap holds', `asked for 500, received ${over.listings.length}`)
      : fail('60-row server cap holds', `received ${over.listings.length}`),
  );

  // 4. No protected data in the public payload. City + state ARE public.
  const leaked = leaks(first.listings);
  cases.push(
    leaked.length === 0
      ? pass('No protected fields in payload', 'no zip, owner id, contact or document path on locked leads (city + state are public by design)')
      : fail('No protected fields in payload', `LEAKED: ${leaked.join(', ')}`),
  );

  // 4b. City + state must actually REACH the cards, otherwise every card
  //     silently degrades to the state-only fallback.
  const withCity = first.listings.filter((l) => (l.city || '').trim()).length;
  cases.push(
    first.listings.length === 0 || withCity > 0
      ? pass('City is visible on public cards', `${withCity}/${first.listings.length} listing(s) return a city for the "City, ST" label`)
      : fail('City is visible on public cards', 'the public feed returned no city at all — cards would fall back to the state name'),
  );


  // 5. Normal browsing must not trip the lockout. The original bug burned a
  //    lockout "strike" on every request, so the 4th call of a session failed.
  const burst: BrowseStatus[] = [];
  for (let i = 0; i < 5; i++) {
    const r = await fetchListingsResult({ limit: 12, offset: 0 });
    burst.push(r.status);
  }
  const blocked = burst.filter((s) => s !== 'ok');
  cases.push(
    blocked.length === 0
      ? pass('Normal session is not rate limited', '6 consecutive requests all returned ok')
      : fail('Normal session is not rate limited', `${blocked.length}/5 calls returned "${blocked[0]}"`),
  );

  // 6. Filtered request still resolves (state filter is server side).
  const filtered = await fetchListingsResult({ state: 'CA', limit: MAX_PAGE_SIZE, offset: 0 });
  cases.push(
    filtered.status === 'ok'
      ? pass('Filtered request resolves', `state=CA returned ${filtered.listings.length} listing(s)`)
      : fail('Filtered request resolves', `status="${filtered.status}"`),
  );

  // 7. Spanish sessions hit the identical endpoint — language must not filter
  //    listings out (listings are stored with their own `language` value and the
  //    feed is language-agnostic by design).
  const langMix = new Set(first.listings.map((l) => l.language).filter(Boolean));
  cases.push(
    pass('Feed is language-agnostic', `languages present in feed: ${[...langMix].join(', ') || 'n/a'} — no client-side language filter is applied`),
  );

  return cases;
};

export const runBrowseRegression = async (): Promise<RegressionCase[]> => {
  const stateCases = runStateMachineCases();
  const liveCases = await runLiveCases();
  return [...stateCases, ...liveCases];
};
